import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Clock, Trash, CircleNotch, Warning, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

export default function TimesheetPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(init());

  function init() {
    return { project_id: "", date: new Date().toISOString().slice(0, 10), hours: 8, description: "" };
  }

  const load = async () => {
    try {
      const r = await publicApi.get(`/portal/timesheet/${token}`);
      setData(r.data);
    } catch { setInvalid(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const log = async () => {
    if (!form.project_id) { toast.error("Pick a project"); return; }
    if (!form.hours || form.hours <= 0) { toast.error("Enter hours"); return; }
    setSaving(true);
    try {
      await publicApi.post(`/portal/timesheet/${token}/log`, form);
      toast.success("Hours logged");
      setForm(init());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const del = async (entryId) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await publicApi.delete(`/portal/timesheet/${token}/${entryId}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center">
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Loading…</div>
      </div>
    );
  }
  if (invalid || !data) {
    return (
      <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-8 shadow-sm text-center">
          <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center bg-amber-50 border border-amber-200">
            <Warning size={22} className="text-amber-600" weight="fill" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-[#0F1E38]">Portal link invalid</h2>
          <p className="mt-2 text-sm text-slate-500">This timesheet link is invalid or has been revoked. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  const person = data.person;
  const projects = data.projects || [];
  const entries = data.entries || [];
  const totalHours = entries.reduce((s, e) => s + (e.hours || 0), 0);
  const thisWeek = entries
    .filter((e) => {
      const d = new Date(e.date);
      const now = new Date();
      const diff = (now - d) / (1000 * 60 * 60 * 24);
      return diff >= 0 && diff < 7;
    })
    .reduce((s, e) => s + (e.hours || 0), 0);
  const projectMap = Object.fromEntries(projects.map((p) => [p.id, p]));

  return (
    <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38]" data-testid="portal-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <header className="mb-8 pb-6 border-b border-slate-200">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-md bg-[#0F1E38] flex items-center justify-center">
              <Clock size={17} className="text-[#FFC107]" weight="bold" />
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.24em] text-slate-500">SoverenX · Timesheet Portal</div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Hi, {person.name.split(" ")[0]}</h1>
          <p className="mt-3 text-sm text-slate-500 max-w-2xl leading-relaxed">
            Log the hours you have worked on any active project. Only you can see these entries.
            {person.role_title && <> Role on record: <span className="font-mono text-[#0F1E38]">{person.role_title}</span>.</>}
          </p>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
          <StatCard label="Total hours logged" value={totalHours.toFixed(1)} />
          <StatCard label="Last 7 days" value={thisWeek.toFixed(1)} />
          <StatCard label="Entries" value={entries.length} />
        </div>

        {/* Log form */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mb-8">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-slate-500 mb-4">Log new entry</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="portal-project" className="mt-1"><SelectValue placeholder="Choose project…" /></SelectTrigger>
                <SelectContent>
                  {projects.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No projects available</div>}
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Hours</Label>
              <Input data-testid="portal-hours" type="number" step="0.25" min="0" max="24" value={form.hours} onChange={(e) => setForm({ ...form, hours: parseFloat(e.target.value) || 0 })} className="mt-1 font-mono" />
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs text-slate-600">What did you work on? <span className="text-slate-400">(optional)</span></Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" placeholder="e.g. Configured Fortinet FGT-100F cluster + failover tests" />
          </div>
          <div className="mt-4 flex justify-end">
            <Button data-testid="portal-log" onClick={log} disabled={saving} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold px-6 h-10">
              {saving ? <><CircleNotch size={14} className="animate-spin mr-2" /> Saving…</> : <><CheckCircle size={14} className="mr-2" weight="bold" /> Log Hours</>}
            </Button>
          </div>
        </div>

        {/* Entries */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          <div className="px-6 py-3 border-b border-slate-200 text-[11px] font-mono uppercase tracking-[0.24em] text-slate-500">Your entries ({entries.length})</div>
          {entries.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              <Clock size={24} className="mx-auto text-slate-400 mb-2" />
              No hours logged yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-2.5">Date</th><th>Project</th><th className="text-right">Hours</th><th>Description</th><th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} data-testid={`portal-row-${e.id}`} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{e.date}</td>
                    <td className="text-slate-700">{projectMap[e.project_id]?.name || "—"}</td>
                    <td className="text-right font-mono text-[#0F1E38]">{e.hours}</td>
                    <td className="text-slate-500 text-xs">{e.description || "—"}</td>
                    <td className="pr-4 text-right">
                      <button data-testid={`portal-del-${e.id}`} onClick={() => del(e.id)} className="text-slate-500 hover:text-red-500" style={{ transition: "color 0.15s ease" }}>
                        <Trash size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6 text-center text-[10px] text-slate-400 font-mono uppercase tracking-wider">
          Private timesheet link · SoverenX Systems · Do not share
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-2xl text-[#0F1E38]">{value}</div>
    </div>
  );
}
